import { Button, Card, Descriptions, Modal, Tag, message, Spin, Table } from "antd";
import React, { Fragment } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import EntitledSection from "../components/EntitledSection";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import entitlements from "../config/entitlements";
import { ColumnsType } from "antd/lib/table";
import { Category, SubCategory } from "../helpers/shared-interfaces";
import { injectKey, request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import { DELETE_SUBCATEGORY, GET_SUBCATEGORY } from "../gql/categories";

interface RouteParams {
  id: string;
}

interface State {
  name: string;
  isLoading: boolean;
  id: string | number;
  showDialog: boolean;
  category?: Category;
  subCategory?: SubCategory;
}

export function SubCategoriesTableFactory(
  data: SubCategory[],
  columns: ColumnsType<SubCategory>,
): JSX.Element {
  return (
    <Table
      bordered={true}
      columns={columns}
      dataSource={injectKey<SubCategory>(data, "id")}
    />
  );
}

function ModalWrapper(
  showDialog: boolean,
  deleteSubcategory: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteSubcategory}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this subCategory?</div>
    </Modal>
  );
}

export default class ViewCategory extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      name: "",
      isLoading: true,
      id: "",
      showDialog: false,
      category: undefined,
      subCategory: undefined,
    };
    this.deleteSubcategory = this.deleteSubcategory.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getSubCategory();
    });
  }

  getSubCategory = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_SUBCATEGORY,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const subCategory = data.data.subCategory;
            this.setState({
              subCategory,
              name: subCategory?.name,
              category: subCategory?.category,
            });
          } else {
            message.error("Failed to get subCategory data");
          }
        });
      },
    );
  };

  deleteSubcategory(): void {
    const { id } = this.state;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_SUBCATEGORY,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data.deleteSubCategory.result) {
              message.success("Subcategory deleted successfuly");
              this.redirectToAllSubCategoriesPage();
            } else {
              message.error("Failed to delete subCategory");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllSubCategoriesPage(): void {
    this.props.history.push("/subCategories");
  }
  render(): JSX.Element | null {
    const { category, isLoading, id, showDialog, subCategory } = this.state;

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              {ModalWrapper(
                showDialog,
                this.deleteSubcategory,
                this.toggleDeleteConfirmationModal,
              )}
              <Descriptions
                title={<div>{category?.name}</div>}
                extra={
                  <div>
                    <EntitledSection entitlement={entitlements.Products.edit}>
                      <NavLink to={`/subCategories/${id}/edit`}>
                        <Button
                          icon={<EditOutlined />}
                          size="large"
                          style={{
                            backgroundColor: "  rgb(29, 55, 100)",
                            color: "white",
                            marginRight: "10px",
                          }}
                          data-cy="test-category-edit-button"
                        >
                          Edit
                        </Button>
                      </NavLink>
                    </EntitledSection>
                    <EntitledSection entitlement={entitlements.Products.delete}>
                      <Button
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={this.toggleDeleteConfirmationModal}
                        data-cy="test-category-delete-button"
                      >
                        Delete
                      </Button>
                    </EntitledSection>
                  </div>
                }
              >
                <Descriptions.Item label={<b>Name</b>} key="Name">
                  {subCategory?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Category</b>} key="Category">
                  {category?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Department</b>} key="Department">
                  {category?.department?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<b>SLA</b>} key="SLA">
                  {subCategory?.sla}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<b>Calculate SLA Before</b>}
                  key="slaCreateByEod"
                >
                  {subCategory?.slaCreateByEod}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Is Active</b>} key="Is Activeame">
                  {category?.isActive.toString()}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Auto-Closeable</b>} key="AutoCloseable">
                  <Tag color={subCategory?.autoCloseable ? "blue" : "default"}>
                    {subCategory?.autoCloseable ? "Yes" : "No"}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
